import { Dialog } from '@spritz-finance/ui'
import { AnimatePresence, motion } from 'framer-motion'
import React, { useCallback, useRef } from 'react'
import { FullAccount } from '../../../../components/FullAccount'
import { useWeb3Connection } from '../../../../web3/Web3Provider'
import {
    Account,
    DirectPayProvider,
    DirectPayProviderMethods,
    DirectPayType,
    useDirectPayContext,
} from './context/DirectPayContext'
import { DirectPayStep } from './context/useDirectPayState'
import { DirectPaySummary } from './DirectPaySummary'
import { CreateDirectPayment } from './form/CreateDirectPayment'

type DirectPayDialogProps = {
    account: Account
    open: boolean
    onClose: () => void
    title?: string
    type?: DirectPayType

    fullAccount?: FullAccount
}

const DirectPayContent = () => {
    const { step } = useDirectPayContext()
    const { chainId, account } = useWeb3Connection()

    let component: React.ReactNode

    switch (step) {
        case DirectPayStep.CREATE:
            component = <CreateDirectPayment key={`${chainId}_${account}`} />
            break
        case DirectPayStep.SUMMARY:
            component = <DirectPaySummary />
            break
        default:
            component = null
            break
    }

    return (
        <AnimatePresence exitBeforeEnter initial={false}>
            <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                key={`${step}`}
            >
                {component}
            </motion.div>
        </AnimatePresence>
    )
}

export const DirectPayDialog: React.VFC<DirectPayDialogProps> = React.memo(
    ({ onClose, open, account, title, type, fullAccount }) => {
        const provider = useRef<DirectPayProviderMethods>(null)

        const handleClose = useCallback(() => {
            provider.current?.handleClose()
        }, [])

        return (
            <>
                <Dialog open={open} onClose={handleClose}>
                    <Dialog.Content
                        minWidth={560}
                        style={{ minHeight: 200 }}
                        contentPadding={false}
                    >
                        <DirectPayProvider
                            ref={provider}
                            account={account}
                            onClose={onClose}
                            title={title}
                            type={type}
                            fullAccount={fullAccount}
                        >
                            <DirectPayContent />
                        </DirectPayProvider>
                    </Dialog.Content>
                </Dialog>
            </>
        )
    }
)
